import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { 
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus, X, 
  Search, Filter, Share2, Printer, Users, Clock, MapPin,
  Video, ChevronDown, AlertTriangle,
  CalendarDays, CalendarRange, Columns, LayoutGrid, SplitSquareVertical,
  Paperclip, Upload, Check, Eye, EyeOff, Settings2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';

// Import from modular calendar components
import {
  API,
  EVENT_TYPES,
  LINKABLE_ENTITY_TYPES,
  getDefaultEventForm,
  populateEventForm,
  getUserColor
} from './calendar/calendarConfig';

import {
  UpcomingEventsWidget,
  CalendarNotificationsPanel,
  MiniCalendar,
  PeopleCalendarsSection,
  TeamCalendarsSection,
  MyCalendarsSection
} from './calendar/CalendarSidebar';

import { ReminderSettings, NotificationPreferencesDialog } from './calendar/NotificationComponents';
import { PlatformLinkingSection } from './calendar/PlatformLinking';
import { DayView, WeekView, WorkWeekView, MultiUserScheduleView, MonthView } from './calendar/CalendarViews';
import { EventDetailPanel } from './calendar/EventDetailPanel';

// ============================================================================
// MAIN CALENDAR V2 COMPONENT
// ============================================================================
const CalendarV2 = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('work_week');
  const [splitView, setSplitView] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailEvent, setDetailEvent] = useState(null);
  const [draggedEvent, setDraggedEvent] = useState(null);
  
  // Conflict detection state
  const [conflicts, setConflicts] = useState([]);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [pendingEvent, setPendingEvent] = useState(null);
  
  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [visibleCalendars, setVisibleCalendars] = useState(['personal', 'team', 'company']);
  
  // Phase 4: Multi-user & Team state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  
  // Phase 5: Notifications state
  const [calendarNotifications, setCalendarNotifications] = useState([]);
  const [showNotificationPrefs, setShowNotificationPrefs] = useState(false);
  const [notificationPreferences, setNotificationPreferences] = useState({
    inApp: true,
    email: true,
    sms: false,
    dailyAgenda: false,
    defaultReminder: 15
  });
  
  // Filters
  const [filterOpen, setFilterOpen] = useState(false);
  const [eventTypeFilter, setEventTypeFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Users and teams for forms
  const [allUsers, setAllUsers] = useState([]);
  const [allTeams, setAllTeams] = useState([]);

  // Event form state - using helper from config
  const [eventForm, setEventForm] = useState(getDefaultEventForm(15));

  // Calendar list for sidebar
  const calendars = [
    { id: 'personal', name: 'My Calendar', color: '#3b82f6' },
    { id: 'team', name: 'Team Calendar', color: '#10b981' },
    { id: 'company', name: 'Company Events', color: '#f59e0b' },
  ];

  // Get date range for current view
  const getDateRange = useCallback(() => {
    const start = new Date(currentDate);
    const end = new Date(currentDate);

    if (view === 'month') {
      start.setDate(1);
      start.setDate(start.getDate() - start.getDay());
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
      end.setDate(end.getDate() + (6 - end.getDay()));
    } else if (view === 'week' || view === 'work_week') {
      const day = start.getDay();
      start.setDate(start.getDate() - day);
      end.setDate(end.getDate() + (6 - day));
    } else {
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
    }

    return { start, end };
  }, [currentDate, view]);

  // Fetch events
  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const { start, end } = getDateRange();
      const response = await axios.get(`${API}/calendar/events`, {
        params: {
          start_date: start.toISOString(),
          end_date: end.toISOString(),
        }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [getDateRange]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/search`);
      setAllUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setAllTeams(response.data);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  useEffect(() => {
    fetchEvents();
    fetchUsers();
    fetchTeams();
  }, [fetchEvents]);

  // ============================================================================
  // EVENT NOTIFICATIONS - Check for upcoming events every minute
  // ============================================================================
  const [notifiedEventIds, setNotifiedEventIds] = useState(new Set());
  
  useEffect(() => {
    // Only run if notifications are enabled
    if (!notificationPreferences.inApp) return;
    
    const checkUpcomingEvents = () => {
      const now = new Date();
      const fifteenMinutesFromNow = new Date(now.getTime() + 15 * 60 * 1000);
      const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000);
      
      events.forEach(event => {
        const eventStart = new Date(event.start_date);
        const eventId = event.id;
        
        // Skip if already notified
        if (notifiedEventIds.has(eventId)) return;
        
        // Skip if event has already started
        if (eventStart <= now) return;
        
        // Check if event is within notification window (5-15 minutes from now)
        if (eventStart <= fifteenMinutesFromNow && eventStart > fiveMinutesFromNow) {
          // Calculate minutes until event
          const minutesUntil = Math.round((eventStart - now) / (1000 * 60));
          
          // Show notification
          const eventType = EVENT_TYPES[event.event_type] || EVENT_TYPES.other;
          toast.info(
            `📅 ${event.title} starts in ${minutesUntil} minutes`,
            {
              description: event.location ? `📍 ${event.location}` : (event.video_link ? '🎥 Video call' : ''),
              duration: 10000,
              action: {
                label: 'View',
                onClick: () => setDetailEvent(event)
              },
              style: {
                borderLeft: `4px solid ${eventType.color}`
              }
            }
          );
          
          // Mark as notified
          setNotifiedEventIds(prev => new Set([...prev, eventId]));
        }
        
        // Urgent 5-minute warning for events very close
        const urgentNotifyKey = `${eventId}-urgent`;
        if (eventStart <= fiveMinutesFromNow && eventStart > now && !notifiedEventIds.has(urgentNotifyKey)) {
          const minutesUntil = Math.round((eventStart - now) / (1000 * 60));
          
          toast.warning(
            `⚡ ${event.title} starts in ${minutesUntil} minute${minutesUntil !== 1 ? 's' : ''}!`,
            {
              description: event.location ? `📍 ${event.location}` : (event.video_link ? '🎥 Click to join video call' : ''),
              duration: 15000,
              action: event.video_link ? {
                label: 'Join Call',
                onClick: () => window.open(event.video_link, '_blank')
              } : {
                label: 'View',
                onClick: () => setDetailEvent(event)
              }
            }
          );
          
          setNotifiedEventIds(prev => new Set([...prev, urgentNotifyKey]));
        }
      });
    };
    
    // Check immediately on mount/events change
    checkUpcomingEvents();
    
    // Set up interval to check every minute
    const interval = setInterval(checkUpcomingEvents, 60000);
    
    return () => clearInterval(interval);
  }, [events, notifiedEventIds, notificationPreferences.inApp]);
  
  // Clear notified events at midnight (reset daily)
  useEffect(() => {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    const msUntilMidnight = midnight - now;
    
    const midnightTimeout = setTimeout(() => {
      setNotifiedEventIds(new Set());
    }, msUntilMidnight);
    
    return () => clearTimeout(midnightTimeout);
  }, []);

  // ============================================================================
  // CONFLICT DETECTION
  // ============================================================================
  const checkConflicts = async (startDate, endDate, excludeEventId = null) => {
    try {
      const params = {
        start_date: startDate,
        end_date: endDate
      };
      if (excludeEventId) {
        params.exclude_event_id = excludeEventId;
      }
      const response = await axios.get(`${API}/calendar/conflicts`, { params });
      return response.data.conflicts || [];
    } catch (error) {
      console.error('Error checking conflicts:', error);
      return [];
    }
  };

  // ============================================================================
  // RSVP HANDLER
  // ============================================================================
  const handleRsvp = async (eventId, status) => {
    try {
      await axios.post(`${API}/calendar/events/${eventId}/rsvp`, null, {
        params: { status }
      });
      toast.success(`RSVP updated to ${status}`);
      fetchEvents();
      if (detailEvent && detailEvent.id === eventId) {
        setDetailEvent({ ...detailEvent, user_rsvp: status });
      }
    } catch (error) {
      console.error('Error updating RSVP:', error);
      toast.error('Failed to update RSVP');
    }
  };

  // ============================================================================
  // EVENT RESIZE HANDLER
  // ============================================================================
  const handleEventResize = async (event, newEndDate) => {
    try {
      await axios.patch(`${API}/calendar/events/${event.id}/reschedule`, null, {
        params: {
          start_date: new Date(event.start_date).toISOString(),
          end_date: newEndDate.toISOString()
        }
      });
      toast.success('Event duration updated');
      fetchEvents();
    } catch (error) {
      console.error('Error resizing event:', error);
      toast.error('Failed to update event duration');
    }
  };

  // Navigation
  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    } else if (view === 'week' || view === 'work_week') {
      newDate.setDate(newDate.getDate() + (7 * direction));
    } else {
      newDate.setDate(newDate.getDate() + direction);
    }
    
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Format date header
  const formatDateHeader = () => {
    if (view === 'month') {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else if (view === 'week' || view === 'work_week') {
      const start = new Date(currentDate);
      const day = start.getDay();
      start.setDate(start.getDate() - day);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else {
      return currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }
  };

  // Event handlers
  const handleEventClick = (event) => {
    if (splitView) {
      setDetailEvent(event);
    } else {
      setSelectedEvent(event);
      setEventForm(populateEventForm(event));
      setShowEventDialog(true);
    }
  };

  const handleDateClick = (date) => {
    const clickDate = new Date(date);
    clickDate.setHours(9, 0, 0, 0);
    const endDate = new Date(clickDate);
    endDate.setHours(10, 0, 0, 0);
    
    setSelectedEvent(null);
    setEventForm({
      ...getDefaultEventForm(notificationPreferences.defaultReminder),
      start_date: clickDate.toISOString().slice(0, 16),
      end_date: endDate.toISOString().slice(0, 16),
    });
    setShowEventDialog(true);
  };

  const handleTimeSlotClick = (date) => {
    const endDate = new Date(date);
    endDate.setHours(date.getHours() + 1);
    
    setSelectedEvent(null);
    setEventForm({
      ...getDefaultEventForm(notificationPreferences.defaultReminder),
      start_date: date.toISOString().slice(0, 16),
      end_date: endDate.toISOString().slice(0, 16),
    });
    setShowEventDialog(true);
  };

  const handleDragStart = (e, event) => {
    setDraggedEvent(event);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDropEvent = async (e, newDate) => {
    e.preventDefault();
    if (!draggedEvent) return;

    const startDate = new Date(draggedEvent.start_date);
    const endDate = new Date(draggedEvent.end_date);
    const duration = endDate - startDate;

    const newStart = new Date(newDate);
    newStart.setHours(startDate.getHours(), startDate.getMinutes());
    const newEnd = new Date(newStart.getTime() + duration);

    try {
      await axios.patch(`${API}/calendar/events/${draggedEvent.id}/reschedule`, null, {
        params: {
          start_date: newStart.toISOString(),
          end_date: newEnd.toISOString()
        }
      });
      toast.success('Event moved');
      fetchEvents();
    } catch (error) {
      console.error('Error moving event:', error);
      toast.error('Failed to move event');
    }

    setDraggedEvent(null);
  };

  const resetEventForm = () => {
    setEventForm(getDefaultEventForm(notificationPreferences.defaultReminder));
  };

  // Phase 6: Platform Linking handlers
  const handleAddLink = (entityType, entityId, entityName) => {
    const config = LINKABLE_ENTITY_TYPES[entityType];
    if (!config) return;
    
    const currentIds = eventForm[config.fieldMultiple] || [];
    const currentNames = eventForm[config.nameMultiple] || [];
    
    if (!currentIds.includes(entityId)) {
      setEventForm(prev => ({
        ...prev,
        [config.fieldMultiple]: [...currentIds, entityId],
        [config.nameMultiple]: [...currentNames, { id: entityId, name: entityName }]
      }));
      toast.success(`Linked to ${entityName}`);
    }
  };

  const handleRemoveLink = (entityType, entityId) => {
    const config = LINKABLE_ENTITY_TYPES[entityType];
    if (!config) return;
    
    setEventForm(prev => ({
      ...prev,
      [config.fieldMultiple]: (prev[config.fieldMultiple] || []).filter(id => id !== entityId),
      [config.nameMultiple]: (prev[config.nameMultiple] || []).filter(item => item.id !== entityId)
    }));
    toast.success('Link removed');
  };

  const handleNavigateToLinkedEntity = (entityType, entityId) => {
    const routes = {
      job: `/jobs/${entityId}`,
      contact: `/contacts/${entityId}`,
      lead: `/leads/${entityId}`,
      eos_meeting: `/eos/l10/${entityId}`,
      eos_rock: `/eos/rocks`,
      eos_todo: `/eos/todos`,
      eos_issue: `/eos/issues`,
      communication: `/communications`,
      call: `/call-center/call-log`,
      chat: `/chat`,
      campaign: `/marketing-hub/campaigns`
    };
    
    const route = routes[entityType];
    if (route) {
      navigate(route);
    }
  };

  const handleCreateEvent = async (e, skipConflictCheck = false) => {
    if (e) e.preventDefault();
    
    if (!eventForm.title || !eventForm.start_date || !eventForm.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Check for conflicts if not skipping
    if (!skipConflictCheck && eventForm.attendees.length > 0) {
      const eventConflicts = await checkConflicts(eventForm.start_date, eventForm.end_date);
      if (eventConflicts.length > 0) {
        setConflicts(eventConflicts);
        setPendingEvent({ ...eventForm });
        setShowConflictDialog(true);
        return;
      }
    }

    try {
      await axios.post(`${API}/calendar/events`, eventForm);
      toast.success('Event created successfully');
      setShowEventDialog(false);
      resetEventForm();
      fetchEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Failed to create event');
    }
  };

  const handleUpdateEvent = async (e, skipConflictCheck = false) => {
    if (e) e.preventDefault();
    
    if (!selectedEvent) return;

    // Check for conflicts if not skipping
    if (!skipConflictCheck && eventForm.attendees.length > 0) {
      const eventConflicts = await checkConflicts(eventForm.start_date, eventForm.end_date, selectedEvent.id);
      if (eventConflicts.length > 0) {
        setConflicts(eventConflicts);
        setPendingEvent({ ...eventForm, id: selectedEvent.id });
        setShowConflictDialog(true);
        return;
      }
    }

    try {
      await axios.put(`${API}/calendar/events/${selectedEvent.id}`, eventForm);
      toast.success('Event updated successfully');
      setShowEventDialog(false);
      setSelectedEvent(null);
      resetEventForm();
      fetchEvents();
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error('Failed to update event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await axios.delete(`${API}/calendar/events/${eventId}`);
      toast.success('Event deleted');
      setShowEventDialog(false);
      setSelectedEvent(null);
      setDetailEvent(null);
      resetEventForm();
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    }
  };

  const handleConflictContinue = () => {
    setShowConflictDialog(false);
    if (pendingEvent) {
      if (pendingEvent.id) {
        handleUpdateEvent(null, true);
      } else {
        handleCreateEvent(null, true);
      }
      setPendingEvent(null);
      setConflicts([]);
    }
  };

  const handleConflictCancel = () => {
    setShowConflictDialog(false);
    setPendingEvent(null);
    setConflicts([]);
  };

  // Notification handlers
  const handleMarkNotificationRead = (notificationId) => {
    setCalendarNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
  };

  const handleMarkAllNotificationsRead = () => {
    setCalendarNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const handleClearNotifications = () => {
    setCalendarNotifications([]);
  };

  const handleSaveNotificationPreferences = (prefs) => {
    setNotificationPreferences(prefs);
    toast.success('Notification preferences saved');
  };

  // Filter events
  const filteredEvents = events.filter(event => {
    if (eventTypeFilter.length > 0 && !eventTypeFilter.includes(event.event_type)) {
      return false;
    }
    if (searchQuery && !event.title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (selectedUsers.length > 0) {
      const isUserEvent = selectedUsers.includes(event.created_by) || 
        (event.attendees && event.attendees.some(id => selectedUsers.includes(id)));
      if (!isUserEvent) return false;
    }
    return true;
  });

  // Toggle handlers
  const toggleCalendarVisibility = (calendarId) => {
    setVisibleCalendars(prev =>
      prev.includes(calendarId)
        ? prev.filter(id => id !== calendarId)
        : [...prev, calendarId]
    );
  };

  const toggleUserFilter = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleTeamFilter = (teamId) => {
    setSelectedTeams(prev =>
      prev.includes(teamId)
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  const toggleEventTypeFilter = (type) => {
    setEventTypeFilter(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  // View icons
  const viewIcons = {
    day: CalendarDays,
    work_week: Columns,
    week: CalendarRange,
    month: LayoutGrid,
    schedule: Users,
  };

  return (
    <Layout>
      <div className="h-[calc(100vh-64px)] flex flex-col">
        {/* Toolbar */}
        <div className="flex-shrink-0 border-b bg-card px-4 py-2">
          <div className="flex items-center justify-between gap-2 min-w-0">
            {/* Left: Navigation */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button variant="outline" size="sm" onClick={goToToday}>
                Today
              </Button>
              <div className="flex items-center">
                <Button variant="ghost" size="icon" onClick={() => navigateDate(-1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => navigateDate(1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <h2 className="text-lg font-semibold whitespace-nowrap">
                {formatDateHeader()}
              </h2>
            </div>

            {/* Center: View Selector */}
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1 flex-shrink-0">
              {Object.entries(viewIcons).map(([key, Icon]) => (
                <TooltipProvider key={key}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={view === key ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setView(key)}
                        className="h-8"
                      >
                        <Icon className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {key.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-1 flex-shrink min-w-0">
              {/* Search */}
              <div className="relative hidden sm:block">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-36 lg:w-48 h-9"
                />
              </div>

              {/* Filter */}
              <DropdownMenu open={filterOpen} onOpenChange={setFilterOpen}>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9">
                    <Filter className="h-4 w-4" />
                    <span className="hidden lg:inline ml-2">Filter</span>
                    {eventTypeFilter.length > 0 && (
                      <Badge variant="secondary" className="ml-1 h-5 px-1">
                        {eventTypeFilter.length}
                      </Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="p-2">
                    <Label className="text-xs text-muted-foreground">Event Types</Label>
                    <div className="mt-2 space-y-1">
                      {Object.entries(EVENT_TYPES).map(([key, type]) => {
                        const Icon = type.icon;
                        return (
                          <label
                            key={key}
                            className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-accent cursor-pointer"
                          >
                            <Checkbox
                              checked={eventTypeFilter.includes(key)}
                              onCheckedChange={() => toggleEventTypeFilter(key)}
                            />
                            <Icon className="h-4 w-4" style={{ color: type.color }} />
                            <span className="text-sm">{type.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setEventTypeFilter([])}>
                    Clear filters
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Split View Toggle */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant={splitView ? 'secondary' : 'outline'}
                      size="sm"
                      onClick={() => setSplitView(!splitView)}
                      className="h-9"
                    >
                      <SplitSquareVertical className="h-4 w-4" />
                      <span className="hidden lg:inline ml-2">Split view</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Toggle split view with event details</TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Notifications */}
              <CalendarNotificationsPanel
                notifications={calendarNotifications}
                onMarkRead={handleMarkNotificationRead}
                onMarkAllRead={handleMarkAllNotificationsRead}
                onClear={handleClearNotifications}
              />

              {/* Notification Preferences */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button variant="ghost" size="icon" onClick={() => setShowNotificationPrefs(true)}>
                      <Settings2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Notification settings</TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <Separator orientation="vertical" className="h-6" />

              {/* Share & Print */}
              <Button variant="ghost" size="icon">
                <Share2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon">
                <Printer className="h-4 w-4" />
              </Button>

              <Separator orientation="vertical" className="h-6" />

              {/* New Event */}
              <Button size="sm" onClick={() => {
                const now = new Date();
                now.setHours(now.getHours() + 1, 0, 0, 0);
                const endDate = new Date(now);
                endDate.setHours(endDate.getHours() + 1);
                
                setSelectedEvent(null);
                setEventForm({
                  ...getDefaultEventForm(notificationPreferences.defaultReminder),
                  start_date: now.toISOString().slice(0, 16),
                  end_date: endDate.toISOString().slice(0, 16),
                });
                setShowEventDialog(true);
              }}>
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline ml-2">New event</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar - Collapsible */}
          {sidebarOpen && (
            <div className="w-56 border-r bg-card flex-shrink-0 flex flex-col">
              <ScrollArea className="flex-1">
                {/* Mini Calendar */}
                <MiniCalendar
                  currentDate={currentDate}
                  onDateSelect={(date) => setCurrentDate(date)}
                  events={events}
                />

                {/* Upcoming Events */}
                <div className="border-t">
                  <UpcomingEventsWidget
                    events={events}
                    onEventClick={handleEventClick}
                  />
                </div>

                {/* My Calendars */}
                <MyCalendarsSection
                  calendars={calendars}
                  visibleCalendars={visibleCalendars}
                  onToggle={toggleCalendarVisibility}
                />

                {/* People Calendars */}
                <PeopleCalendarsSection
                  users={allUsers}
                  selectedUsers={selectedUsers}
                  onToggleUser={toggleUserFilter}
                  currentUserId={user?.id}
                />

                {/* Team Calendars */}
                <TeamCalendarsSection
                  teams={allTeams}
                  selectedTeams={selectedTeams}
                  onToggleTeam={toggleTeamFilter}
                />
              </ScrollArea>

              {/* Sidebar Toggle - More prominent */}
              <div className="p-2 border-t bg-muted/50">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-center gap-2"
                  onClick={() => setSidebarOpen(false)}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Hide Panel
                </Button>
              </div>
            </div>
          )}

          {/* Sidebar collapsed toggle - More visible */}
          {!sidebarOpen && (
            <div className="w-10 border-r bg-muted/30 flex-shrink-0 flex flex-col items-center py-4 gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setSidebarOpen(true)}
                      className="h-8 w-8"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>Show sidebar</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}

          {/* Calendar Content */}
          <div className="flex-1 flex min-w-0">
            <div className="flex-1 overflow-hidden min-w-0">
              {loading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <>
                  {view === 'day' && (
                    <DayView
                      date={currentDate}
                      events={filteredEvents}
                      onEventClick={handleEventClick}
                      onDragStart={handleDragStart}
                      onTimeSlotClick={handleTimeSlotClick}
                      onDropEvent={handleDropEvent}
                      onEventResize={handleEventResize}
                    />
                  )}
                  {view === 'work_week' && (
                    <WorkWeekView
                      startDate={currentDate}
                      events={filteredEvents}
                      onEventClick={handleEventClick}
                      onDragStart={handleDragStart}
                      onTimeSlotClick={handleTimeSlotClick}
                      onDropEvent={handleDropEvent}
                      onEventResize={handleEventResize}
                    />
                  )}
                  {view === 'week' && (
                    <WeekView
                      startDate={currentDate}
                      events={filteredEvents}
                      onEventClick={handleEventClick}
                      onDragStart={handleDragStart}
                      onTimeSlotClick={handleTimeSlotClick}
                      onDropEvent={handleDropEvent}
                      onEventResize={handleEventResize}
                    />
                  )}
                  {view === 'month' && (
                    <MonthView
                      date={currentDate}
                      events={filteredEvents}
                      onEventClick={handleEventClick}
                      onDragStart={handleDragStart}
                      onDateClick={handleDateClick}
                      onDropEvent={handleDropEvent}
                    />
                  )}
                  {view === 'schedule' && (
                    <MultiUserScheduleView
                      date={currentDate}
                      events={filteredEvents}
                      selectedUsers={selectedUsers}
                      allUsers={allUsers}
                      onEventClick={handleEventClick}
                      onDragStart={handleDragStart}
                      onTimeSlotClick={handleTimeSlotClick}
                      onDropEvent={handleDropEvent}
                      onEventResize={handleEventResize}
                    />
                  )}
                </>
              )}
            </div>

            {/* Split View Detail Panel */}
            {splitView && (
              <div className="w-80 border-l bg-card flex-shrink-0">
                <EventDetailPanel
                  event={detailEvent}
                  onClose={() => setDetailEvent(null)}
                  onEdit={(event) => {
                    setSelectedEvent(event);
                    setEventForm(populateEventForm(event));
                    setShowEventDialog(true);
                  }}
                  onDelete={handleDeleteEvent}
                  onRsvp={handleRsvp}
                  onNavigate={handleNavigateToLinkedEntity}
                />
              </div>
            )}
          </div>
        </div>

        {/* Event Create/Edit Dialog */}
        <Dialog open={showEventDialog} onOpenChange={(open) => {
          setShowEventDialog(open);
          if (!open) {
            setSelectedEvent(null);
            resetEventForm();
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedEvent ? 'Edit Event' : 'Create Event'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={selectedEvent ? handleUpdateEvent : handleCreateEvent}>
              <div className="space-y-4 py-4">
                {/* Title */}
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={eventForm.title}
                    onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                    placeholder="Event title"
                    required
                  />
                </div>

                {/* Date/Time Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Start *</Label>
                    <Input
                      id="start_date"
                      type="datetime-local"
                      value={eventForm.start_date}
                      onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">End *</Label>
                    <Input
                      id="end_date"
                      type="datetime-local"
                      value={eventForm.end_date}
                      onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })}
                      required
                    />
                  </div>
                </div>

                {/* All Day Toggle */}
                <div className="flex items-center gap-2">
                  <Switch
                    id="all_day"
                    checked={eventForm.all_day}
                    onCheckedChange={(checked) => setEventForm({ ...eventForm, all_day: checked })}
                  />
                  <Label htmlFor="all_day">All day event</Label>
                </div>

                {/* Event Type & Priority */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Event Type</Label>
                    <Select
                      value={eventForm.event_type}
                      onValueChange={(value) => setEventForm({ ...eventForm, event_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(EVENT_TYPES).map(([key, type]) => {
                          const Icon = type.icon;
                          return (
                            <SelectItem key={key} value={key}>
                              <div className="flex items-center gap-2">
                                <Icon className="h-4 w-4" style={{ color: type.color }} />
                                {type.label}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Priority</Label>
                    <Select
                      value={eventForm.priority}
                      onValueChange={(value) => setEventForm({ ...eventForm, priority: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select priority" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Location & Video */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="location" className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Location
                    </Label>
                    <Input
                      id="location"
                      value={eventForm.location}
                      onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                      placeholder="Enter location"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="video_link" className="flex items-center gap-2">
                      <Video className="h-4 w-4" />
                      Video Link
                    </Label>
                    <Input
                      id="video_link"
                      value={eventForm.video_link}
                      onChange={(e) => setEventForm({ ...eventForm, video_link: e.target.value })}
                      placeholder="Zoom, Meet, or Teams link"
                    />
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={eventForm.description}
                    onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                    placeholder="Add description..."
                    rows={3}
                  />
                </div>

                {/* Attendees */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Attendees
                  </Label>
                  <div className="flex flex-wrap gap-2 p-2 border rounded-md min-h-[40px]">
                    {eventForm.attendees.map(userId => {
                      const attendee = allUsers.find(u => u.id === userId);
                      if (!attendee) return null;
                      const userColor = getUserColor(userId);
                      return (
                        <Badge key={userId} variant="secondary" className="gap-1">
                          <Avatar className="h-4 w-4">
                            <AvatarImage src={attendee.profile_picture} />
                            <AvatarFallback 
                              className="text-[8px]"
                              style={{ backgroundColor: userColor.lightColor, color: userColor.color }}
                            >
                              {attendee.first_name?.[0]}{attendee.last_name?.[0]}
                            </AvatarFallback>
                          </Avatar>
                          {attendee.first_name}
                          <X
                            className="h-3 w-3 cursor-pointer hover:text-destructive"
                            onClick={() => setEventForm({
                              ...eventForm,
                              attendees: eventForm.attendees.filter(id => id !== userId)
                            })}
                          />
                        </Badge>
                      );
                    })}
                  </div>
                  <Select
                    onValueChange={(userId) => {
                      if (!eventForm.attendees.includes(userId)) {
                        setEventForm({
                          ...eventForm,
                          attendees: [...eventForm.attendees, userId]
                        });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Add attendee..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allUsers.filter(u => !eventForm.attendees.includes(u.id)).map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.first_name} {user.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* RSVP Required */}
                <div className="flex items-center gap-2">
                  <Switch
                    id="rsvp_required"
                    checked={eventForm.rsvp_required}
                    onCheckedChange={(checked) => setEventForm({ ...eventForm, rsvp_required: checked })}
                  />
                  <Label htmlFor="rsvp_required">Request RSVP from attendees</Label>
                </div>

                {/* Reminders */}
                <ReminderSettings
                  reminders={eventForm.reminders || []}
                  onChange={(reminders) => setEventForm({ ...eventForm, reminders })}
                />

                {/* Platform Integration - Link to Platform */}
                <PlatformLinkingSection
                  linkedEntities={eventForm}
                  onAddLink={handleAddLink}
                  onRemoveLink={handleRemoveLink}
                  onNavigate={handleNavigateToLinkedEntity}
                />

                {/* Attachments */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Attachments
                  </Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center hover:bg-accent/50 transition-colors cursor-pointer">
                    <Upload className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Drop files here or click to upload
                    </p>
                  </div>
                  {eventForm.attachments.length > 0 && (
                    <div className="space-y-1">
                      {eventForm.attachments.map((attachment, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-muted rounded text-sm">
                          <Paperclip className="h-4 w-4" />
                          <span className="flex-1 truncate">{attachment.split('/').pop()}</span>
                          <X
                            className="h-4 w-4 cursor-pointer hover:text-destructive"
                            onClick={() => setEventForm({
                              ...eventForm,
                              attachments: eventForm.attachments.filter((_, i) => i !== idx)
                            })}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Public Event Toggle */}
                <div className="flex items-center gap-2">
                  <Switch
                    id="is_public"
                    checked={eventForm.is_public}
                    onCheckedChange={(checked) => setEventForm({ ...eventForm, is_public: checked })}
                  />
                  <Label htmlFor="is_public">Make this event visible to everyone</Label>
                </div>
              </div>

              <DialogFooter className="gap-2">
                {selectedEvent && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => handleDeleteEvent(selectedEvent.id)}
                  >
                    Delete
                  </Button>
                )}
                <Button type="button" variant="outline" onClick={() => setShowEventDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {selectedEvent ? 'Update Event' : 'Create Event'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Conflict Warning Dialog */}
        <Dialog open={showConflictDialog} onOpenChange={setShowConflictDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-5 w-5" />
                Schedule Conflict Detected
              </DialogTitle>
              <DialogDescription>
                This event overlaps with existing events. Do you want to continue anyway?
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <Alert variant="warning" className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Conflicting Events</AlertTitle>
                <AlertDescription>
                  <div className="mt-2 space-y-2">
                    {conflicts.map((conflict, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <Clock className="h-4 w-4" />
                        <span className="font-medium">{conflict.title}</span>
                        <span className="text-muted-foreground">
                          {new Date(conflict.start_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} -
                          {new Date(conflict.end_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </span>
                      </div>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleConflictCancel}>
                Go Back
              </Button>
              <Button variant="destructive" onClick={handleConflictContinue}>
                Create Anyway
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Notification Preferences Dialog */}
        <NotificationPreferencesDialog
          open={showNotificationPrefs}
          onOpenChange={setShowNotificationPrefs}
          preferences={notificationPreferences}
          onSave={handleSaveNotificationPreferences}
        />
      </div>
    </Layout>
  );
};

export default CalendarV2;
